import React, { useContext, useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";

import { toast } from "react-toastify";
import openSocket from "socket.io-client";
import clsx from "clsx";

import { Paper, makeStyles } from "@material-ui/core";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { EditingMessageProvider } from "../../context/EditingMessage/EditingMessageContext";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

import TicketHandlingModal from "../TicketHandlingModal";
import { SpyModeContext } from "../../context/SpyMode/SpyModeContext";
import { mergeContactPreservingManualName } from "../../helpers/contactIdentity";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  ticketInfo: {
    maxWidth: "50%",
    flexBasis: "50%",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "80%",
      flexBasis: "80%",
    },
  },
  ticketActionButtons: {
    maxWidth: "50%",
    flexBasis: "50%",
    left: "-50px",
    position: "relative",
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      flexBasis: "100%",
      marginBottom: "5px",
    },
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();

  const { setSpyMode } = useContext(SpyModeContext);
  const { user } = useContext(AuthContext);

  const [modalTicketHandlingOpen, setTicketHandlingModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});

  useEffect(() => {
    if(ticket.openHandlingModal === true) {
      setTicketHandlingModalOpen(true);
      setSpyMode(true);
    } else {
      setTicketHandlingModalOpen(false);
      setSpyMode(false);
    }
  }, [ticket]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          const { data } = await api.get("/tickets/" + ticketId);
          
          const isAdminOrSuperadmin = user?.profile !== "user";
          
          setContact(data.contact);
          setTicket(data);
          setLoading(false);
          
          if (isAdminOrSuperadmin) {
            setSpyMode(false);
          }
          
        } catch (err) {
          history.push("/tickets");
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, history, user]);

  useEffect(() => {
    const socket = openSocket(process.env.REACT_APP_BACKEND_URL);

    socket.on("connect", () => socket.emit("joinChatBox", ticketId));

    socket.on("appMessage", (data) => {
      if (data.action === "create") {
        if (data.message?.ticket?.id) {
          setTicket((prev) => {
            const next = { ...prev, ...data.message.ticket };
            if (data.message.ticket.contact) {
              next.contact = mergeContactPreservingManualName(
                prev.contact,
                data.message.ticket.contact
              );
            }
            return next;
          });
          if (data.message.ticket.contact) {
            setContact((prev) =>
              mergeContactPreservingManualName(prev, data.message.ticket.contact)
            );
          }
        }
      }
    });

    socket.on("ticket", (data) => {
      if (data.action === "update") {
        // Mescla com estado atual para não perder associações (contact, whatsapp)
        // que podem vir ausentes em eventos parciais (ex: CloseGroupTicketsOnDisconnect)
        if (data.ticket?.id) {
          setTicket(prev => ({ ...prev, ...data.ticket }));
        }
      }

      if (data.action === "delete") {
        toast.success("Conversa deletada com sucesso!");
        history.push("/tickets");
      }
    });

    socket.on("contact", (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return mergeContactPreservingManualName(prevState, data.contact);
          }
          return prevState;
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId, history]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleSpyTicket = async (id) => {
	  history.push(`/tickets/${id}`);
	};

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <TicketHandlingModal
          open={modalTicketHandlingOpen}
          onClose={() => {
            setTicketHandlingModalOpen(false);
            handleSpyTicket(ticket.id);
          }}
          ticket={ticket}
        ></TicketHandlingModal>
        <TicketHeader loading={loading}>
          <div className={classes.ticketInfo}>
            <TicketInfo
              contact={contact}
              ticket={ticket}
              onClick={handleDrawerOpen}
            />
          </div>
          <div className={classes.ticketActionButtons}>
            <TicketActionButtons ticket={ticket} />
          </div>
        </TicketHeader>
        <ReplyMessageProvider>
          <EditingMessageProvider>
            <MessagesList
              ticketId={ticketId}
              isGroup={ticket.isGroup}
              ticket={ticket}
            ></MessagesList>
            <MessageInput contact={contact} ticketStatus={ticket.status} ticket={ticket} />
          </EditingMessageProvider>
        </ReplyMessageProvider>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        ticket={ticket}
        loading={loading}
      />
    </div>
  );
};

export default Ticket;
